import { useMemo, useState } from "react";
import type { Peer } from "../../_components/scoring-types";
import { DriverAccordionList } from "./DriverAccordionList";
import { DriverToolbar } from "./DriverToolbar";
import type { CategoryDetail, SortMode } from "./scoring-category-types";

interface SubcategoryDriverListProps {
  detailData: CategoryDetail | null;
  accountName: string;
  peerColumns: (Peer | null)[];
  isLoading: boolean;
  error: string | null;
}

export function SubcategoryDriverList({
  detailData,
  accountName,
  peerColumns,
  isLoading,
  error,
}: SubcategoryDriverListProps) {
  const [activeSubcategoryIdx, setActiveSubcategoryIdx] = useState(0);
  const [sortMode, setSortMode] = useState<SortMode>("default");
  const [allExpanded, setAllExpanded] = useState(false);
  const subcategories = detailData?.subcategories ?? [];
  const activeSubcategory = subcategories[activeSubcategoryIdx];

  const handleFilterBySubcategory = (idx: number) => {
    setActiveSubcategoryIdx(idx);
    setSortMode("default");
    setAllExpanded(false);
  };

  // Sort drivers
  const sortedDrivers = useMemo(() => {
    if (!activeSubcategory) return [];
    const drivers = [...activeSubcategory.kpi_drivers];
    if (sortMode === "by-gap") {
      drivers.sort((a, b) => a.account_score - b.account_score);
    }
    return drivers;
  }, [activeSubcategory, sortMode]);

  if (isLoading) {
    return (
      <div className="space-y-2 py-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={`skeleton-${i}`}
            className="h-[56px] rounded-xl bg-muted/50 animate-pulse"
            style={{ animationDelay: `${i * 100}ms` }}
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-destructive py-8 text-center">{error}</div>
    );
  }

  return (
    <div>
      {subcategories.length > 0 && activeSubcategory && (
        <DriverToolbar
          subcategories={subcategories}
          activeSubcategoryIdx={activeSubcategoryIdx}
          onFilterBySubcategory={handleFilterBySubcategory}
          sortMode={sortMode}
          onSortChange={setSortMode}
          allExpanded={allExpanded}
          onToggleExpand={() => setAllExpanded((v) => !v)}
        />
      )}

      {sortedDrivers.length > 0 ? (
        <DriverAccordionList
          drivers={sortedDrivers}
          peerColumns={peerColumns}
          accountName={accountName}
          expandAll={allExpanded}
        />
      ) : (
        <div className="text-sm text-muted-foreground py-8 text-center">
          No KPI drivers available for this category.
        </div>
      )}
    </div>
  );
}
